// Press-and-hold reordering for the prep editor's sections, on pointer events so
// a mouse and a finger behave alike. The six-dot grip lifts the section the
// moment it is pressed (Adam, 2026-10-04: "don't require the 6 dots to be held
// for quite so long to reorder"); the heading, which is also a text box, still
// wants a 250 ms hold without moving. Once lifted, a copy follows the pointer, the real one stays
// in the list as an empty gap that moves to where it would land, and the list
// scrolls when the pointer nears its top or bottom edge. A short tap never
// reaches here, so it still focuses the heading.

import { dropIndex, moveId, sameOrder } from "./prepEdit";

export const HOLD_MS = 250;
/** Movement before the hold is up that means "this is a scroll or a text
 * selection", not a drag. */
const MOVE_TOLERANCE = 8;
const EDGE = 64;

export interface DragHost {
  /** The scrolling element the sections sit in. */
  root(): HTMLElement | null;
  /** The element holding the sections one under another. */
  list(): HTMLElement | null;
  section(id: string): HTMLElement | null;
  order(): string[];
  /** The order to show while dragging, or null when the drag is over. */
  preview(order: string[] | null, id: string | null): void;
  /** A drag that changed the order ended. */
  drop(order: string[]): void;
}

interface Run {
  id: string;
  pid: number;
  x: number;
  y: number;
  cx: number;
  cy: number;
  active: boolean;
  timer: ReturnType<typeof setTimeout>;
  order0: string[];
  current: string[];
  heights: Map<string, number>;
  listTop: number;
  grab: number;
  ghost: HTMLElement | null;
  raf: number;
}

export interface PointerStart {
  readonly clientX: number;
  readonly clientY: number;
  readonly pointerId: number;
  readonly pointerType: string;
  readonly button: number;
}

export class SectionDrag {
  /** A drag is under way (the hold was reached). */
  active = false;
  private run: Run | null = null;
  private readonly host: DragHost;

  constructor(host: DragHost) {
    this.host = host;
  }

  /** `hold` is how long the press must last before the section lifts; 0 lifts
   * it at once (the grip, which has nothing else to do with a press). */
  start(e: PointerStart, id: string, hold: number = HOLD_MS): void {
    if (this.run) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const run: Run = {
      id,
      pid: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      cx: e.clientX,
      cy: e.clientY,
      active: false,
      timer: setTimeout(() => this.begin(), hold),
      order0: [],
      current: [],
      heights: new Map(),
      listTop: 0,
      grab: 0,
      ghost: null,
      raf: 0,
    };
    this.run = run;
    window.addEventListener("pointermove", this.onMove, true);
    window.addEventListener("pointerup", this.onUp, true);
    window.addEventListener("pointercancel", this.onCancel, true);
    window.addEventListener("keydown", this.onKey, true);
    window.addEventListener("contextmenu", this.onContext, true);
    if (hold === 0) {
      clearTimeout(run.timer);
      this.begin();
    }
  }

  /** Ends any drag without dropping, for an unmount. */
  dispose(): void {
    this.finish(false);
  }

  private onContext = (ev: Event): void => {
    if (this.run) ev.preventDefault();
  };

  private onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape" && this.run?.active) {
      ev.preventDefault();
      this.finish(false);
    }
  };

  private onTouchMove = (ev: TouchEvent): void => {
    if (this.run?.active && ev.cancelable) ev.preventDefault();
  };

  private onMove = (ev: PointerEvent): void => {
    const run = this.run;
    if (!run || ev.pointerId !== run.pid) return;
    run.cx = ev.clientX;
    run.cy = ev.clientY;
    if (!run.active) {
      if (Math.hypot(ev.clientX - run.x, ev.clientY - run.y) > MOVE_TOLERANCE) this.finish(false);
      return;
    }
    ev.preventDefault();
    this.update();
  };

  private onUp = (ev: PointerEvent): void => {
    if (this.run && ev.pointerId === this.run.pid) this.finish(true);
  };

  private onCancel = (ev: PointerEvent): void => {
    if (this.run && ev.pointerId === this.run.pid) this.finish(false);
  };

  private begin(): void {
    const run = this.run;
    if (!run) return;
    const root = this.host.root();
    const list = this.host.list();
    const el = this.host.section(run.id);
    if (!root || !list || !el) {
      this.finish(false);
      return;
    }
    run.active = true;
    this.active = true;
    (document.activeElement as HTMLElement | null)?.blur?.();
    window.getSelection()?.removeAllRanges();

    const rootRect = root.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
    run.order0 = this.host.order();
    run.current = run.order0;
    for (const id of run.order0) {
      const s = this.host.section(id);
      run.heights.set(id, (s?.getBoundingClientRect().height ?? 0) + gap);
    }
    run.listTop = list.getBoundingClientRect().top - rootRect.top + root.scrollTop;
    const rect = el.getBoundingClientRect();
    run.grab = run.cy - rect.top;

    const ghost = el.cloneNode(true) as HTMLElement;
    ghost.classList.add("pe__ghost");
    ghost.removeAttribute("data-sec");
    ghost.setAttribute("aria-hidden", "true");
    ghost.querySelectorAll("[contenteditable]").forEach((n) => n.setAttribute("contenteditable", "false"));
    const from = el.querySelectorAll("input");
    ghost.querySelectorAll("input").forEach((n, i) => {
      n.value = from[i]?.value ?? "";
      n.tabIndex = -1;
    });
    ghost.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;`;
    root.appendChild(ghost);
    run.ghost = ghost;
    root.classList.add("pe--dragging");
    window.addEventListener("touchmove", this.onTouchMove, { passive: false });
    try {
      navigator.vibrate?.(8);
    } catch {
      // No haptics here.
    }
    this.host.preview(run.order0, run.id);
    this.loop();
  }

  private update(): void {
    const run = this.run;
    const root = this.host.root();
    if (!run || !run.active || !root) return;
    if (run.ghost) run.ghost.style.top = `${run.cy - run.grab}px`;
    const rootRect = root.getBoundingClientRect();
    const y = run.cy - rootRect.top + root.scrollTop;
    const others = run.order0.filter((x) => x !== run.id);
    const idx = dropIndex(y, run.listTop, others.map((x) => run.heights.get(x) ?? 0));
    const next = moveId(run.order0, run.id, idx);
    if (!sameOrder(next, run.current)) {
      run.current = next;
      this.host.preview(next, run.id);
    }
  }

  private loop(): void {
    const step = (): void => {
      const run = this.run;
      const root = this.host.root();
      if (!run || !run.active || !root) return;
      const rect = root.getBoundingClientRect();
      let dy = 0;
      if (run.cy < rect.top + EDGE) dy = -Math.ceil((rect.top + EDGE - run.cy) / 5);
      else if (run.cy > rect.bottom - EDGE) dy = Math.ceil((run.cy - (rect.bottom - EDGE)) / 5);
      dy = Math.max(-24, Math.min(24, dy));
      if (dy !== 0) {
        root.scrollTop += dy;
        this.update();
      }
      run.raf = requestAnimationFrame(step);
    };
    if (this.run) this.run.raf = requestAnimationFrame(step);
  }

  private finish(commit: boolean): void {
    const run = this.run;
    if (!run) return;
    this.run = null;
    clearTimeout(run.timer);
    cancelAnimationFrame(run.raf);
    window.removeEventListener("pointermove", this.onMove, true);
    window.removeEventListener("pointerup", this.onUp, true);
    window.removeEventListener("pointercancel", this.onCancel, true);
    window.removeEventListener("keydown", this.onKey, true);
    window.removeEventListener("touchmove", this.onTouchMove);
    // Leaving the contextmenu guard up a moment keeps the long press's own
    // menu from opening as the finger lifts.
    setTimeout(() => window.removeEventListener("contextmenu", this.onContext, true), 400);
    if (!run.active) return;
    run.ghost?.remove();
    this.host.root()?.classList.remove("pe--dragging");
    if (commit && !sameOrder(run.current, run.order0)) this.host.drop(run.current);
    this.host.preview(null, null);
    // The click that follows a lifted pointer must not focus the heading.
    const swallow = (ev: Event): void => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    window.addEventListener("click", swallow, true);
    setTimeout(() => {
      window.removeEventListener("click", swallow, true);
      this.active = false;
    }, 350);
    this.active = true;
  }
}
